import BusinessIcon from '@material-design-icons/svg/filled/business.svg?react';
import VisibilityOffIcon from '@material-design-icons/svg/filled/visibility_off.svg?react';
import { delay } from 'es-toolkit/promise';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useAsyncData } from '../../hooks/useAsyncData';
import { useAuth } from '../../hooks/useAuth';
import { usePublisher } from '../../hooks/usePublisher';
import { useS3Image } from '../../hooks/useS3Image';
import { useTRPC } from '../../hooks/useTRPC';

import { captureAndReportError } from '../../utils/errorReporting';

interface OrganizationFormData {
  publisherName: string;
  website: string;
  avatar?: File | null;
}

interface IndividualFormData {
  name: string;
  website: string;
  avatar?: File | null;
}

type FormData = OrganizationFormData | IndividualFormData;

export const PublisherProfilePage = () => {
  const [authState] = useAuth();
  const { activePublisher } = usePublisher();
  const trpc = useTRPC();
  const [formData, setFormData] = useState<FormData | null>(null);
  const [publisherType, setPublisherType] = useState<
    'Individual' | 'Organization' | null
  >(null);
  const [showWebsiteUrl, setShowWebsiteUrl] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

  const {
    imageUrl: avatarImageUrl,
    isLoading: avatarLoading,
    error: avatarError,
  } = useS3Image(avatarPreview);
  const {
    data: kybStatus,
    isLoading,
    setIsLoading,
    run,
  } = useAsyncData<{
    status:
      | 'NotStarted'
      | 'InProgress'
      | 'UnderReview'
      | 'Approved'
      | 'Rejected'
      | 'RequiresAdditionalInfo';
    submittedAt?: string;
    reviewStartedAt?: string;
    reviewCompletedAt?: string;
    rejectionReason?: string;
    additionalInfoRequested?: string;
  } | null>({
    initialData: null,
    initialLoading: true,
  });

  useEffect(() => {
    const loadPublisherData = async () => {
      if (authState.type !== 'Authenticated') {
        if (authState.type === 'NoToken') {
          setIsLoading(false);
        }
        return;
      }

      await delay(100);

      try {
        if (activePublisher) {
          setPublisherType(activePublisher.publisherType);

          if (activePublisher.publisherType === 'Organization') {
            const orgData: OrganizationFormData = {
              publisherName: activePublisher.name,
              website: activePublisher.website || '',
              avatar: null,
            };
            setFormData(orgData);
          } else {
            const indData: IndividualFormData = {
              name: activePublisher.name,
              website: activePublisher.website || '',
              avatar: null,
            };
            setFormData(indData);
            setAvatarPreview(
              activePublisher.avatarThumbnailUrl ||
                activePublisher.avatarUrl ||
                null,
            );
          }
        } else {
          setPublisherType(null);
          setFormData(null);
          setAvatarPreview(null);
        }
      } catch (error) {
        captureAndReportError('Failed to load publisher data:', error);
        toast.error('Failed to load publisher profile');
      }

      await run(() => trpc.getKybStatus.query(undefined), {
        showLoadingState: false,
        onSuccess: (kybResult, helpers) => {
          if ('_tag' in kybResult && kybResult._tag === 'Right') {
            helpers.setData(kybResult.right);
          }
          helpers.setIsLoading(false);
        },
        onError: (error, helpers) => {
          captureAndReportError('Failed to load KYB status:', error);
          helpers.setIsLoading(false);
        },
      });
    };

    loadPublisherData();
  }, [activePublisher, authState, run, setIsLoading, trpc]);

  const isOrganizationData = (
    data: FormData,
    type: 'Individual' | 'Organization' | null,
  ): data is OrganizationFormData => {
    return type === 'Organization' && 'publisherName' in data;
  };

  const toggleShowWebsite = () => setShowWebsiteUrl(!showWebsiteUrl);

  if (isLoading || authState.type === 'Verifying') {
    return (
      <div className="min-h-full bg-neutral-900 text-white flex items-center justify-center">
        <div className="text-neutral-400">Loading publisher profile…</div>
      </div>
    );
  }

  if (!formData || !publisherType) {
    return (
      <div className="min-h-full bg-neutral-900 text-white flex items-center justify-center">
        <div className="text-neutral-400">No publisher profile found</div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      <div className="bg-neutral-900 h-[72px] flex-shrink-0 sticky top-0 z-40">
        <div className="h-full flex items-center justify-between px-10">
          <div className="flex items-center gap-2">
            <BusinessIcon className="size-5 fill-white" />
            <span className="text-white font-medium text-base">Publisher</span>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="p-10">
          <div className="max-w-2xl mx-auto">
            <div className="mb-8">
              <p className="text-sm text-neutral-400">
                Seeker needs to verify your information to comply with
                regulations.
              </p>
            </div>

            <div className="mb-8 bg-neutral-800 border border-neutral-700 rounded-lg p-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-bold text-white mb-2">
                    Publisher Type
                  </h3>
                  <div className="flex items-center gap-2">
                    <span className="text-white">{publisherType}</span>
                    <span className="text-xs text-neutral-400">
                      ({publisherType === 'Individual' ? 'KYC' : 'KYB'}{' '}
                      required)
                    </span>
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-bold text-white mb-2">
                    Verification Status
                  </h3>
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        kybStatus?.status === 'Approved'
                          ? 'bg-green-900/50 text-green-400'
                          : kybStatus?.status === 'Rejected'
                            ? 'bg-red-900/50 text-red-400'
                            : kybStatus?.status === 'UnderReview'
                              ? 'bg-yellow-900/50 text-yellow-400'
                              : kybStatus?.status === 'InProgress'
                                ? 'bg-blue-900/50 text-blue-400'
                                : kybStatus?.status === 'RequiresAdditionalInfo'
                                  ? 'bg-orange-900/50 text-orange-400'
                                  : 'bg-neutral-700 text-neutral-300'
                      }`}
                    >
                      {kybStatus?.status === 'NotStarted'
                        ? 'Not Started'
                        : kybStatus?.status === 'InProgress'
                          ? 'In Progress'
                          : kybStatus?.status === 'UnderReview'
                            ? 'Under Review'
                            : kybStatus?.status === 'RequiresAdditionalInfo'
                              ? 'Additional Info Required'
                              : kybStatus?.status || 'Not Started'}
                    </span>
                  </div>
                  {kybStatus?.submittedAt && (
                    <p className="text-xs text-neutral-400 mt-1">
                      Submitted:{' '}
                      {new Date(kybStatus.submittedAt).toLocaleDateString()}
                    </p>
                  )}
                  {kybStatus?.rejectionReason &&
                    kybStatus?.status === 'Rejected' && (
                      <p className="text-xs text-red-400 mt-2">
                        Reason: {kybStatus.rejectionReason}
                      </p>
                    )}
                  {kybStatus?.additionalInfoRequested &&
                    kybStatus?.status === 'RequiresAdditionalInfo' && (
                      <p className="text-xs text-orange-400 mt-2">
                        Additional info needed:{' '}
                        {kybStatus.additionalInfoRequested}
                      </p>
                    )}
                </div>
              </div>
            </div>

            <div className="space-y-10">
              <div className="space-y-6">
                <div className="space-y-1">
                  <label className="block text-sm font-bold text-white">
                    {publisherType === 'Organization' ? 'Name' : 'Name'}
                  </label>
                  <input
                    type="text"
                    aria-label="Name"
                    value={
                      publisherType === 'Organization' &&
                      isOrganizationData(formData, publisherType)
                        ? formData.publisherName
                        : !isOrganizationData(formData, publisherType)
                          ? formData.name
                          : ''
                    }
                    className="w-full h-10 px-4 bg-neutral-800 border border-neutral-700 rounded-lg text-neutral-400 cursor-not-allowed"
                    readOnly
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-sm font-bold text-white">
                    Website
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      aria-label="Website"
                      value={formData.website}
                      className="w-full h-10 px-4 pr-12 bg-neutral-800 border border-neutral-700 rounded-lg text-neutral-400 cursor-not-allowed"
                      placeholder="https://"
                      readOnly
                    />
                    <button
                      type="button"
                      onClick={toggleShowWebsite}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2"
                    >
                      <VisibilityOffIcon className="size-4 fill-current text-white mix-blend-multiply" />
                    </button>
                  </div>
                </div>

                {/* Avatar - Individual only */}
                {publisherType === 'Individual' && avatarPreview && (
                  <div className="space-y-1">
                    <label className="block text-sm font-bold text-white">
                      Avatar
                    </label>
                    <div className="w-full h-28 bg-neutral-800 border border-neutral-700 rounded-lg p-4">
                      {avatarLoading ? (
                        <div className="w-full h-full flex items-center justify-center">
                          <div className="text-neutral-400 text-sm">
                            Loading avatar…
                          </div>
                        </div>
                      ) : avatarError ? (
                        <div className="w-full h-full flex items-center justify-center">
                          <div className="text-red-400 text-sm">
                            Failed to load avatar
                          </div>
                        </div>
                      ) : avatarImageUrl ? (
                        <img
                          src={avatarImageUrl}
                          alt="Avatar"
                          className="w-full h-full object-contain rounded"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <div className="text-neutral-400 text-sm">
                            No avatar available
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
